import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useSettings } from "../contexts/SettingsContext";
import { ShoppingBag, User, Stethoscope, Sparkles, LogOut, Menu, X, ChevronDown, Briefcase, UserCog, KeyRound } from "lucide-react";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import SearchBar from "./SearchBar";

export default function Header() {
    const { user, logout } = useAuth();
    const { count } = useCart();
    const { settings } = useSettings();
    const nav = useNavigate();
    const [open, setOpen] = React.useState(false);

    const linkBase = "text-sm tracking-wide hover:text-sky-700 transition-colors";
    const NAV = [
        { to: "/shop", label: "Shop" },
        { to: "/ai-doctor", label: "AI Doctor", icon: Sparkles },
        { to: "/doctors", label: "Doctors", icon: Stethoscope },
        { to: "/blog", label: "Journal" },
        { to: "/track", label: "Track order" },
        { to: "/enroll-doctor", label: "Enroll as Doctor" },
    ];

    // Capture UTM tags (and ?coupon=) on every page load so checkout can attribute the order.
    React.useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        const u = {
            utm_source: p.get("utm_source"),
            utm_medium: p.get("utm_medium"),
            utm_campaign: p.get("utm_campaign"),
            utm_content: p.get("utm_content"),
        };
        if (Object.values(u).some(Boolean)) {
            sessionStorage.setItem("hpl_utm", JSON.stringify(u));
        }
        const c = p.get("coupon");
        if (c) sessionStorage.setItem("hpl_coupon", c.trim().toUpperCase());
    }, []);

    return (
        <header className="sticky top-0 z-40 bg-stone-50/85 backdrop-blur-xl border-b border-stone-200" data-testid="site-header">
            <div className="bg-sky-700 text-sky-50 text-xs">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-between flex-wrap gap-x-4">
                    <span className="tracking-wider">Free shipping on orders ≥ ₹999</span>
                    <a href={`mailto:${settings.contact_email || "help@hplmedicines.com"}`} className="hover:text-white transition-colors" data-testid="header-help-email">
                        Need help? <span className="underline underline-offset-2 font-medium">{settings.contact_email || "help@hplmedicines.com"}</span>
                    </a>
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
                <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
                    {settings.logo_url ? (
                        <img src={settings.logo_url} alt={settings.brand_name} className="w-8 h-8 object-contain" />
                    ) : (
                        <span className="w-8 h-8 grid place-items-center bg-sky-700 text-stone-50 font-serif text-lg">{(settings.brand_name || "H").charAt(0)}</span>
                    )}
                    <div className="leading-tight">
                        <div className="font-serif text-xl text-stone-900">{settings.brand_name}</div>
                        <div className="text-[9px] tracking-[0.3em] uppercase text-stone-500">{settings.brand_subtitle}</div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    {NAV.map((l) => (
                        <NavLink key={l.to} to={l.to} className={({ isActive }) => `${linkBase} ${isActive ? "text-sky-700 font-medium" : "text-stone-700"}`} data-testid={`nav-${l.to.replace('/', '')}`}>
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <Link to="/cart" className="relative p-2 hover:text-sky-700" data-testid="header-cart">
                        <ShoppingBag className="w-5 h-5" />
                        {count > 0 && (
                            <span className="absolute -top-1 -right-1 bg-sky-700 text-stone-50 text-[10px] w-4 h-4 rounded-full grid place-items-center" data-testid="cart-count">{count}</span>
                        )}
                    </Link>
                    {user ? (
                        <div className="flex items-center gap-2">
                            {user.role === "doctor" && (
                                <Link to="/doctor" data-testid="nav-doctor-dashboard">
                                    <Button variant="outline" size="sm" className="rounded-none border-stone-300"><Stethoscope className="w-4 h-4 mr-1" />Dashboard</Button>
                                </Link>
                            )}
                            {user.role === "customer" && (
                                <>
                                    <Link to="/orders" data-testid="nav-my-orders">
                                        <Button variant="outline" size="sm" className="rounded-none border-stone-300"><User className="w-4 h-4 mr-1" />My Orders</Button>
                                    </Link>
                                    <Link to="/my-appointments" data-testid="nav-my-appointments">
                                        <Button variant="outline" size="sm" className="rounded-none border-stone-300"><Stethoscope className="w-4 h-4 mr-1" />Appointments</Button>
                                    </Link>
                                </>
                            )}
                            <Link to="/account/change-password" data-testid="nav-change-password" title="Change password">
                                <Button variant="outline" size="sm" className="rounded-none border-stone-300"><KeyRound className="w-4 h-4" /></Button>
                            </Link>
                            <Button onClick={() => { logout(); nav("/"); }} variant="ghost" size="sm" className="rounded-none" data-testid="logout-btn"><LogOut className="w-4 h-4" /></Button>
                        </div>
                    ) : (
                        <>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="rounded-none" data-testid="signin-dropdown">Sign in <ChevronDown className="w-3 h-3 ml-1" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="rounded-none w-56">
                                    <DropdownMenuLabel className="text-[10px] tracking-widest uppercase">Choose your portal</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem asChild data-testid="signin-customer"><Link to="/login?portal=customer" className="flex items-center gap-2"><ShoppingBag className="w-4 h-4" />Customer / Doctor</Link></DropdownMenuItem>
                                    <DropdownMenuItem asChild data-testid="signin-distributor"><Link to="/login?portal=distributor" className="flex items-center gap-2"><Briefcase className="w-4 h-4" />Distributor</Link></DropdownMenuItem>
                                    <DropdownMenuItem asChild data-testid="signin-employee"><Link to="/login?portal=employee" className="flex items-center gap-2"><UserCog className="w-4 h-4" />HPL Employee</Link></DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel className="text-[10px] tracking-widest uppercase">New here?</DropdownMenuLabel>
                                    <DropdownMenuItem asChild data-testid="signup-customer-link"><Link to="/register" className="flex items-center gap-2 text-stone-700"><ShoppingBag className="w-4 h-4" />Create customer account</Link></DropdownMenuItem>
                                    <DropdownMenuItem asChild data-testid="signup-staff-link"><Link to="/staff-register" className="flex items-center gap-2 text-sky-700 font-medium"><UserCog className="w-4 h-4" />HPL staff sign-up</Link></DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <Link to="/register" data-testid="nav-register"><Button size="sm" className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50">Create account</Button></Link>
                        </>
                    )}
                </div>

                <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-stone-200 bg-stone-50">
                    <div className="px-4 py-4 flex flex-col gap-3">
                        {NAV.map((l) => (
                            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm py-1" data-testid={`mnav-${l.to.replace('/', '')}`}>{l.label}</Link>
                        ))}
                        <Link to="/cart" onClick={() => setOpen(false)} className="text-sm py-1">Cart ({count})</Link>
                        {user ? (
                            <>
                                {user.role === "doctor" && <Link to="/doctor" onClick={() => setOpen(false)} className="text-sm py-1">Doctor Dashboard</Link>}
                                {user.role === "customer" && <Link to="/orders" onClick={() => setOpen(false)} className="text-sm py-1">My Orders</Link>}
                                {user.role === "customer" && <Link to="/subscriptions" onClick={() => setOpen(false)} className="text-sm py-1">Auto-refills</Link>}
                                <Link to="/account/change-password" onClick={() => setOpen(false)} className="text-sm py-1">Change password</Link>
                                <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="text-sm py-1 text-left">Sign out</button>
                            </>
                        ) : (
                            <>
                                <Link to="/login?portal=customer" onClick={() => setOpen(false)} className="text-sm py-1">Sign in (Customer)</Link>
                                <Link to="/login?portal=distributor" onClick={() => setOpen(false)} className="text-sm py-1">Sign in (Distributor)</Link>
                                <Link to="/login?portal=employee" onClick={() => setOpen(false)} className="text-sm py-1">Sign in (Employee)</Link>
                                <Link to="/register" onClick={() => setOpen(false)} className="text-sm py-1">Create customer account</Link>
                                <Link to="/staff-register" onClick={() => setOpen(false)} className="text-sm py-1 text-sky-700 font-medium">HPL staff sign-up</Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
